import {
  Component,
  Input,
  input,
  output,
  OnInit,
  OnDestroy,
  ViewChild,
  computed,
  InputSignal,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { ElementRef } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { DatePicker, DatePickerModule } from '@openng/optimus-ui/datepicker';

import { AnimationEvent } from '@angular/animations';
import { noop } from 'rxjs';

@Component({
  selector: 'phar-dropdown-timepicker2',
  templateUrl: './phar-dropdown-timepicker2.component.html',
  styleUrls: ['./phar-dropdown-timepicker2.component.scss'],
  standalone: true,
  imports: [DatePickerModule, FormsModule],
})
export class PharDropdownTimepicker2Component implements ControlValueAccessor, OnInit, OnDestroy {
  private _focusMonitor = inject(FocusMonitor);
  private _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  ngControl = inject(NgControl, { optional: true, self: true });

  id: InputSignal<string> = input('time-picker');
  @ViewChild('calendar') calendar!: DatePicker;

  focused = false;
  touched = false;

  placeholderSignal = input<string>('', { alias: 'placeholder' });
  /** When enabled, displays a button with icon next to input.*/
  showIcon = input<boolean>(true);
  /** Whether to show the seconds in time picker.*/
  showSeconds = input<boolean>(false);
  /**When specified, prevents entering the date manually with keyboard.*/
  readonlyInput = input<boolean>(false);
  icon = input<string>('icon-clock');
  hourFormat = input<'12' | '24'>('24');
  /** Default time value in "hh:mm:ss" format (e.g., "14:30:00") */
  defaultTime = input<string | null>();
  /** Minimum selectable time value in "hh:mm:ss" format (e.g., "08:00:00") */
  minTime = input<string | null>();
  /** Maximum selectable time value in "hh:mm:ss" format (e.g., "18:00:00") */
  maxTime = input<string | null>();

  onSelect = output<string>();
  onBlur = output<Event>();
  onFocus = output<Event>();
  onClose = output<AnimationEvent>();
  onShow = output<AnimationEvent>();

  defaultDate = computed(() => {
    if (this.defaultTime()) {
      return this.stringToDate(this.defaultTime());
    }
    const today = new Date();
    if (!this.showSeconds()) {
      today.setSeconds(0);
    }
    return today;
  });
  minDate = computed(() => {
    return this.stringToDate(this.minTime() || '');
  });
  maxDate = computed(() => {
    return this.stringToDate(this.maxTime() || '');
  });

  private _value: string | null = null;
  private _internalDateValue: Date | null = null;
  private _required = false;
  private _disabled = false;

  private onTouched: () => void = noop;
  private onChange: (_: string | null) => void = noop;

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this._focusMonitor.monitor(this._elementRef, true).subscribe(origin => {
      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
    });
  }

  ngOnDestroy(): void {
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  @Input()
  get value(): string | null {
    return this._value;
  }
  set value(timeString: string | null) {
    if (timeString !== this._value) {
      this._value = timeString;
      this._internalDateValue = this.stringToDate(timeString || '');
      this.onChange(timeString);
    }
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }

  get internalDateValue(): Date | null {
    return this._internalDateValue;
  }

  writeValue(value: string | null): void {
    this._value = value;
    this._internalDateValue = this.stringToDate(value || '');
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  onDateSelect(date: Date): void {
    const timeString = this.dateToString(date);
    this.value = timeString;
    if (timeString) {
      this.onSelect.emit(timeString);
    }
  }

  onCalendarFocus(event: Event): void {
    this.focused = true;
    this.onFocus.emit(event);
  }

  onCalendarBlur(event: Event): void {
    this.touched = true;
    this.focused = false;
    this.onTouched();
    this.onBlur.emit(event);
  }

  onCalendarClose(event: AnimationEvent): void {
    this.onClose.emit(event);
  }

  onCalendarShow(event: AnimationEvent): void {
    this.alignOverlay(event);
    this.onShow.emit(event);
  }

  private alignOverlay(event: AnimationEvent): void {
    const origin: HTMLElement = this._elementRef.nativeElement;
    const overlay: HTMLElement = event && event.element;

    if (!origin || !overlay) {
      return;
    }
    overlay.style.width = origin.offsetWidth + 'px';
    if (origin.offsetWidth <= 120) {
      overlay.style.setProperty('--p-datepicker-time-picker-gap', '0');
      overlay.style.setProperty('--p-datepicker-time-picker-button-gap', '0');
    }

    const originOffset = origin.getBoundingClientRect();
    const overlayRelativeParentOffset = overlay.offsetParent.getBoundingClientRect();
    const viewport = {
      height: window.innerHeight || document.documentElement.clientHeight,
      width: window.innerWidth || document.documentElement.clientWidth,
    };

    let top: number, left: number;
    if (originOffset.top + originOffset.height + overlay.offsetHeight > viewport.height) {
      top = originOffset.top - overlayRelativeParentOffset.top - overlay.offsetHeight;
      overlay.style.transformOrigin = 'bottom';
      if (overlayRelativeParentOffset.top + top < 0) {
        top = -1 * overlayRelativeParentOffset.top;
      }
    } else {
      top = originOffset.top + originOffset.height - overlayRelativeParentOffset.top;
      overlay.style.transformOrigin = 'top';
    }

    const horizontalOverflow = originOffset.left + overlay.offsetWidth - viewport.width;
    if (horizontalOverflow > 0) {
      // element wider then viewport but can be fit on screen (align at right side of viewport)
      left = originOffset.left - overlayRelativeParentOffset.left - horizontalOverflow;
    } else {
      // element fits on screen (align with target)
      left = originOffset.left - overlayRelativeParentOffset.left;
    }

    overlay.style.top = top + 'px';
    overlay.style.left = left + 'px';
  }

  private stringToDate(timeString: string): Date | null {
    if (!timeString) return null;

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/;
    const match = timeString.match(timeRegex);

    if (!match) {
      console.error(`Invalid time format: ${timeString}. Format should be HH:MM:SS`);
      return null;
    }

    const [, hours, minutes, seconds] = match;
    const today = new Date();
    today.setHours(parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10), 0);

    return today;
  }

  private dateToString(date: Date): string | null {
    if (!date) return null;

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  }
}
